<template>
  <NcrmContractSideEditableRow
    class="ncrm-onboarding-owner-field"
    :label="$t('ncrm.onboarding.details.context.owner')"
    icon="mdi-account-tie-outline"
    :editing="editing"
    @start="startOwnerEdit"
  >
    <template #editor>
      <v-autocomplete
        v-model="draftOwnerUserId"
        :items="ownerItems"
        item-title="label"
        item-value="value"
        density="compact"
        hide-details
        autofocus
        menu-icon=""
        class="mt-1"
        :loading="ownerLoading"
        :disabled="saving || !onboardingId"
        :no-data-text="$t('ncrm.onboarding.details.owner.noOptions')"
        :placeholder="$t('ncrm.onboarding.details.owner.placeholder')"
        @click.stop
        @keydown.esc="cancelOwnerEdit"
        @update:search="scheduleOwnerSearch"
        @focus="ensureOwnersLoaded"
      >
        <template #append-inner>
          <v-btn icon size="x-small" variant="text" color="success" :disabled="!canSaveOwner" :loading="saving" @click.stop="saveOwner">
            <v-icon size="14">mdi-check</v-icon>
          </v-btn>
          <v-btn icon size="x-small" variant="text" :disabled="saving" @click.stop="cancelOwnerEdit">
            <v-icon size="14">mdi-close</v-icon>
          </v-btn>
        </template>
      </v-autocomplete>
    </template>
    {{ ownerDisplayLabel }}
  </NcrmContractSideEditableRow>
</template>

<script>
import axios from "axios";
import NcrmContractSideEditableRow from "@/components/ncrm/contracts/NcrmContractSideEditableRow.vue";
import { parsePositiveId } from "./onboardingTemplateFormShared.mjs";

const ownerLabelFromRecord = (item = {}) => {
  const record = item || {};
  const label = String(
    record.owner_name
    || record.display_name
    || record.name
    || record.email
    || ""
  ).trim();
  if (label) return label;
  const ownerId = parsePositiveId(record.owner_user_id ?? record.user_id ?? record.id);
  return ownerId ? `#${ownerId}` : "-";
};

const normalizeOwnerOption = (item = {}) => {
  const record = item || {};
  const value = parsePositiveId(record.value ?? record.id ?? record.user_id ?? record.owner_user_id);
  if (!value) return null;
  const label = ownerLabelFromRecord(item);
  return { value, label: label === "-" ? `#${value}` : label };
};

export default {
  name: "NcrmOnboardingOwnerField",
  components: {
    NcrmContractSideEditableRow,
  },
  props: {
    onboardingId: {
      type: [Number, String],
      default: null,
    },
    item: {
      type: Object,
      default: null,
    },
  },
  emits: ["updated", "notify"],
  data() {
    return {
      draftOwnerUserId: null,
      ownerItems: [],
      ownerLoading: false,
      ownerRequestSeq: 0,
      ownerSearchTimer: null,
      saving: false,
      editing: false,
    };
  },
  computed: {
    currentOwnerUserId() {
      return parsePositiveId(this.item?.owner_user_id);
    },
    canSaveOwner() {
      const draftOwnerId = parsePositiveId(this.draftOwnerUserId);
      return Boolean(draftOwnerId && draftOwnerId !== this.currentOwnerUserId && !this.saving && this.onboardingId);
    },
    ownerDisplayLabel() {
      const directLabel = ownerLabelFromRecord(this.item);
      if (directLabel && directLabel !== "-") return directLabel;
      const currentOwnerId = this.currentOwnerUserId;
      const option = this.ownerItems.find((entry) => entry.value === currentOwnerId);
      return option?.label || "-";
    },
  },
  watch: {
    item: {
      immediate: true,
      handler(nextItem) {
        this.draftOwnerUserId = parsePositiveId(nextItem?.owner_user_id);
        this.mergeOwnerOptions([nextItem]);
      },
    },
  },
  beforeUnmount() {
    if (this.ownerSearchTimer) clearTimeout(this.ownerSearchTimer);
  },
  methods: {
    startOwnerEdit() {
      this.editing = true;
      this.draftOwnerUserId = this.currentOwnerUserId;
      this.mergeOwnerOptions([this.item]);
      this.ensureOwnersLoaded();
    },
    cancelOwnerEdit() {
      this.draftOwnerUserId = this.currentOwnerUserId;
      this.editing = false;
    },
    mergeOwnerOptions(items = []) {
      const optionsById = new Map(this.ownerItems.map((entry) => [entry.value, entry]));
      for (const item of Array.isArray(items) ? items : []) {
        const option = normalizeOwnerOption(item);
        if (option) optionsById.set(option.value, option);
      }
      this.ownerItems = Array.from(optionsById.values()).sort((left, right) =>
        String(left.label || "").localeCompare(String(right.label || ""), this.$i18n?.locale || undefined)
      );
    },
    ensureOwnersLoaded() {
      if (!this.ownerItems.length) this.loadOwners();
    },
    scheduleOwnerSearch(search) {
      if (this.ownerSearchTimer) clearTimeout(this.ownerSearchTimer);
      this.ownerSearchTimer = setTimeout(() => this.loadOwners(search), 220);
    },
    async loadOwners(search = "") {
      const requestSeq = this.ownerRequestSeq + 1;
      this.ownerRequestSeq = requestSeq;
      this.ownerLoading = true;
      try {
        const { data } = await axios.get("/ncrm/onboarding/owners", {
          params: { search: String(search || "").trim() || undefined, limit: 100 },
        });
        if (requestSeq === this.ownerRequestSeq) {
          this.mergeOwnerOptions(Array.isArray(data?.items) ? data.items : []);
        }
      } catch (error) {
        const message = error?.response?.data?.error || this.$t("ncrm.onboarding.details.messages.ownerLoadError");
        this.$emit("notify", { message, color: "error" });
      } finally {
        if (requestSeq === this.ownerRequestSeq) this.ownerLoading = false;
      }
    },
    async saveOwner() {
      const ownerUserId = parsePositiveId(this.draftOwnerUserId);
      if (!ownerUserId || !this.onboardingId || !this.canSaveOwner) return;
      this.saving = true;
      try {
        const { data } = await axios.patch(`/ncrm/onboarding/${this.onboardingId}/owner`, {
          owner_user_id: ownerUserId,
        });
        if (data?.item) {
          this.mergeOwnerOptions([data.item]);
          this.$emit("updated", data.item);
        }
        const message = this.$t("ncrm.onboarding.details.messages.ownerUpdated");
        this.editing = false;
        this.$emit("notify", { message, color: "success" });
      } catch (error) {
        const message = error?.response?.data?.error || this.$t("ncrm.onboarding.details.messages.ownerUpdateError");
        this.$emit("notify", { message, color: "error" });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.ncrm-onboarding-owner-field.ncrm-contract-side-editable-row {
  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  border-radius: 16px;
}
</style>
